import { useEffect, useState } from "react";

import type { I_Country, I_Location, I_RadiusLocation, I_Status } from "../shared/types.ts";
import { fetchCountries, fetchRadius, fetchSession, fetchStatus, logout, startImport } from "./api.ts";
import CountryFilter from "./components/CountryFilter.tsx";
import ExportBar from "./components/ExportBar.tsx";
import Login from "./components/Login.tsx";
import OriginPicker, { describe } from "./components/OriginPicker.tsx";
import RadiusControl from "./components/RadiusControl.tsx";
import ResultTable from "./components/ResultTable.tsx";

const formatDate = (value: string | null): string => {
    if (!value) return "nie";

    return new Date(value).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

const App = () => {
    const [session, setSession] = useState<{ authRequired: boolean, loggedIn: boolean } | null>(null);
    const [status, setStatus] = useState<I_Status | null>(null);
    const [countries, setCountries] = useState<I_Country[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [includeOrgs, setIncludeOrgs] = useState(false);
    const [origin, setOrigin] = useState<I_Location | null>(null);
    const [radiusKm, setRadiusKm] = useState(25);
    const [locations, setLocations] = useState<I_RadiusLocation[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [importing, setImporting] = useState(false);

    const loadStatus = () => {
        fetchStatus().then(setStatus).catch(() => undefined);
        fetchCountries().then(setCountries).catch(() => undefined);
    };

    useEffect(() => {
        fetchSession().then(setSession).catch(() => setSession({ authRequired: true, loggedIn: false }));
    }, []);

    useEffect(() => {
        if (session?.loggedIn) loadStatus();
    }, [session?.loggedIn]);

    useEffect(() => {
        if (!session?.loggedIn) return;
        if (!origin) {
            setLocations([]);

            return;
        }

        setLoading(true);

        const timer = setTimeout(() => {
            fetchRadius(origin, radiusKm, selected, includeOrgs)
                .then((result) => {
                    setLocations(result.locations);
                    setTruncated(result.truncated);
                    setError("");
                })
                .catch((problem) => setError(problem.message))
                .finally(() => setLoading(false));
        }, 250);

        return () => clearTimeout(timer);
    }, [origin, radiusKm, selected, includeOrgs, session?.loggedIn]);

    const refresh = async () => {
        setImporting(true);

        const result = await startImport();

        setImporting(false);
        loadStatus();
        setError(result.status === "failed" ? result.note : "");
    };

    const signOut = async () => {
        await logout();
        setSession({ authRequired: true, loggedIn: false });
        setOrigin(null);
        setStatus(null);
    };

    if (!session) return null; // Solange offen ist, ob eine Anmeldung nötig ist, nichts zeigen
    if (!session.loggedIn) return <Login onSuccess={() => setSession({ ...session, loggedIn: true })} />;

    return (
        <div className="app">
            <header>
                <div>
                    <h1>TBGeoData</h1>
                    <p className="subtitle">Orte im Umkreis aus den GeoNames-Postleitzahldaten</p>
                </div>

                {status && (
                    <div className="status">
                        <span><strong>{status.rows.toLocaleString("de-DE")}</strong> Orte aus {status.countries} Ländern</span>
                        <span>Letzter Import {formatDate(status.lastImportAt)}</span>
                        <span>Nächste Prüfung {formatDate(status.nextRunAt)}</span>
                        <button onClick={refresh} disabled={importing || status.importRunning}>
                            {importing || status.importRunning ? "Import läuft …" : "Jetzt prüfen"}
                        </button>
                        {session.authRequired && <button className="logout" onClick={signOut}>Abmelden</button>}
                    </div>
                )}
            </header>

            {error && <p className="error banner">{error}</p>}

            <section className="controls">
                <OriginPicker origin={origin} countries={selected} includeOrgs={includeOrgs} onSelect={setOrigin} />
                <CountryFilter available={countries} selected={selected} onChange={setSelected} />
                <RadiusControl value={radiusKm} onChange={setRadiusKm} />
            </section>

            <section className="options">
                <label>
                    <input type="checkbox" checked={includeOrgs} onChange={(event) => setIncludeOrgs(event.target.checked)} />
                    Großempfänger einbeziehen
                    <span className="note">Firmen und Behörden mit eigener Postleitzahl, {status ? status.orgs.toLocaleString("de-DE") : "…"} Einträge</span>
                </label>

                {origin && <button className="link" onClick={() => setOrigin(null)}>Startort {describe(origin)} zurücksetzen</button>}
            </section>

            <ExportBar origin={origin} locations={locations} radiusKm={radiusKm} countries={selected} truncated={truncated} />

            <ResultTable locations={locations} loading={loading} />

            <footer>
                Ortsdaten von <a href="https://www.geonames.org" target="_blank" rel="noreferrer">GeoNames</a>,
                lizenziert unter <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a>
            </footer>
        </div>
    )
}
export default App;